import { memo, useId, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Box, ButtonBase } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import {
  MISSING,
  toDebugInfo,
} from "src/_modules/AiSupportChat/core/debugInfo";

const MONO_FONT =
  'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

const yesNo = (value, t) => {
  if (value === null) return MISSING;
  return value ? t("debug.yes") : t("debug.no");
};

/** A technical value (ids, "4,193 / 288", "812 ms"): LTR inside any layout. */
const Ltr = ({ children }) => <bdi dir="ltr">{children}</bdi>;

/**
 * Summary line of the collapsed block: the numbers looked at most. Each part
 * is isolated, so «·» separators never reorder numbers in the Arabic layout.
 */
const summaryOf = (info, t) =>
  [
    <Ltr>{info.model}</Ltr>,
    <Ltr>{info.total}</Ltr>,
    <Trans
      t={t}
      i18nKey="debug.tokensShort"
      values={{ tokens: info.tokens }}
      components={{ ltr: <Ltr /> }}
    />,
    t("debug.topShort", { score: info.topScore }),
    info.fallbackUsed && t("debug.fallbackShort"),
    info.flags.length > 0 && t("debug.flagsShort", { count: info.flags.length }),
  ]
    .filter(Boolean)
    .map((part, index) => (
      <span key={index}>
        {index > 0 ? " · " : null}
        {part}
      </span>
    ));

const CELL_SX = {
  p: 0,
  py: 0.25,
  textAlign: "start",
  verticalAlign: "top",
  overflowWrap: "anywhere",
};

const Flag = ({ children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      px: 0.5,
      borderRadius: 0.5,
      border: 1,
      borderColor: "warning.main",
      bgcolor: "rgba(237, 108, 2, 0.08)",
    }}
  >
    {children}
  </Box>
);

const ChunksTable = ({ chunks, t }) => (
  <Box
    component="table"
    aria-label={t("debug.chunks")}
    sx={{
      width: "100%",
      mt: 1,
      borderCollapse: "collapse",
      tableLayout: "fixed",
      "& th": { ...CELL_SX, color: "text.secondary", fontWeight: 400 },
      "& td": { ...CELL_SX, borderTop: 1, borderColor: "divider" },
      "& th + th, & td + td": { paddingInlineStart: 1 },
    }}
  >
    <thead>
      <tr>
        <Box component="th" scope="col" sx={{ width: "45%" }}>
          {t("debug.chunk")}
        </Box>
        <Box component="th" scope="col">
          {t("debug.file")}
        </Box>
        <Box component="th" scope="col" sx={{ width: "4.5em" }}>
          {t("debug.score")}
        </Box>
      </tr>
    </thead>
    <tbody>
      {chunks.map((chunk, index) => (
        <tr key={index}>
          {/* Titles can be Arabic or English: their own direction */}
          <td dir="auto">{chunk.title}</td>
          <td>
            <Ltr>{chunk.file}</Ltr>
          </td>
          <td>
            <Ltr>{chunk.score}</Ltr>
          </td>
        </tr>
      ))}
    </tbody>
  </Box>
);

/**
 * `done.debug` of one answer (plan §3.8), for AI developers only: a
 * collapsible block, small and monospace, under the answer. Collapsed it
 * shows model · total latency · tokens · top score (+ fallback, flags);
 * expanded, every field and the retrieved chunks. Flags are highlighted.
 *
 * Presentational (props only), so the admin transcript viewer reuses it.
 * Never mount it for merchants (non-negotiable #3): the caller decides.
 * Labels follow the app language and direction (T21); technical values are
 * LTR isolates, and Arabic values (chunk titles, the condensed query) keep
 * their own direction.
 *
 * @param {object} props
 * @param {object} props.debug `done.debug` as the server sent it (any shape
 *   is shown safely, core/debugInfo)
 * @param {boolean} [props.defaultExpanded]
 */
function DebugPanel({ debug, defaultExpanded = false }) {
  const { t, i18n } = useTranslation("aiSupport");
  const [expanded, setExpanded] = useState(defaultExpanded);
  const contentId = useId();
  const info = useMemo(() => toDebugInfo(debug), [debug]);
  if (!info) return null;

  const rows = [
    ["profile", info.profile],
    ["provider", info.provider],
    ["model", info.model],
    ["fallbackUsed", yesNo(info.fallbackUsed, t)],
    ["promptVersion", info.promptVersion],
    ["indexVersion", info.indexVersion],
    ["ttft", info.ttft],
    ["total", info.total],
    [
      "tokens",
      info.tokensEstimated ? (
        <>
          <Ltr>{info.tokens}</Ltr> {t("debug.estimated")}
        </>
      ) : (
        info.tokens
      ),
    ],
    ["cost", info.cost],
    ["retrievalMode", info.retrievalMode],
    ["topScore", info.topScore],
    ["condensedQuery", info.condensedQuery, true],
  ];

  return (
    <Box
      data-ai-debug=""
      lang={i18n.language}
      sx={(theme) => ({
        mt: 1,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "grey.50",
        color: "text.primary",
        fontFamily: theme.typography.fontFamily,
        fontSize: 11,
        lineHeight: 1.5,
        // Technical values in monospace; labels and Arabic prose (chunk
        // titles, the condensed query) stay in the app font: monospace
        // fallbacks space Arabic glyphs apart
        "& bdi[dir='ltr']": { fontFamily: MONO_FONT },
      })}
    >
      <ButtonBase
        aria-expanded={expanded}
        aria-controls={expanded ? contentId : undefined}
        onClick={() => setExpanded((open) => !open)}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          gap: 0.5,
          px: 0.75,
          py: 0.5,
          borderRadius: 1,
          font: "inherit",
          textAlign: "start",
          "&.Mui-focusVisible": {
            outline: (theme) => `2px solid ${theme.palette.primary.main}`,
            outlineOffset: "-2px",
          },
        }}
      >
        <ExpandMore
          aria-hidden="true"
          sx={{
            fontSize: 16,
            flexShrink: 0,
            transform: expanded ? "rotate(180deg)" : "none",
          }}
        />
        <Box component="span" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
          <Box component="span" sx={{ fontWeight: 700 }}>
            {t("debug.title")}
          </Box>{" "}
          {summaryOf(info, t)}
        </Box>
      </ButtonBase>

      {expanded && (
        <Box id={contentId} sx={{ px: 1, pb: 1 }}>
          <Box
            component="dl"
            sx={{
              display: "grid",
              gridTemplateColumns: "max-content minmax(0, 1fr)",
              columnGap: 1.5,
              rowGap: 0.25,
              m: 0,
              "& dt": { color: "text.secondary" },
              "& dd": { m: 0, overflowWrap: "anywhere" },
            }}
          >
            {rows.map(([key, value, ownDirection]) => (
              <Box key={key} sx={{ display: "contents" }}>
                <dt>{t(`debug.rows.${key}`)}</dt>
                {ownDirection ? (
                  <dd dir="auto">{value}</dd>
                ) : (
                  <dd>
                    {typeof value === "string" ? <Ltr>{value}</Ltr> : value}
                  </dd>
                )}
              </Box>
            ))}
            <dt>{t("debug.rows.flags")}</dt>
            <Box
              component="dd"
              sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
            >
              {info.flags.length > 0
                ? info.flags.map((flag, index) => (
                    <Flag key={index}>
                      <Ltr>{flag}</Ltr>
                    </Flag>
                  ))
                : t("debug.none")}
            </Box>
          </Box>
          {info.chunks.length > 0 ? (
            <ChunksTable chunks={info.chunks} t={t} />
          ) : (
            <Box sx={{ mt: 1, color: "text.secondary" }}>
              {t("debug.noChunks")}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default memo(DebugPanel);
